import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { useUpdateTenant } from "@/hooks/useTenantQueries";
import { Tenant } from "@/services/admin-tenant.service";
import { Skeleton } from "@/components/ui/skeleton";

// ============================================================================
// Validation Schema
// ============================================================================

const editTenantSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters").max(100),
  contactEmail: z.string().optional(),
});

type EditTenantFormData = z.infer<typeof editTenantSchema>;

// ============================================================================
// Component
// ============================================================================

interface TenantEditFormProps {
  tenant: Tenant | undefined;
  isLoading?: boolean;
  onSuccess?: () => void;
}

export function TenantEditForm({
  tenant,
  isLoading,
  onSuccess,
}: TenantEditFormProps) {
  const updateTenant = useUpdateTenant(tenant?.id || "");
  // `updateTenant.isPending` only disables the submit button after a render, so a fast
  // double-click can fire `onSubmit` a second time before that paints.
  const isSubmittingRef = useRef(false);

  const form = useForm<EditTenantFormData>({
    resolver: zodResolver(editTenantSchema),
    defaultValues: {
      name: "",
      contactEmail: "",
    },
  });

  // Update form values when tenant data loads
  useEffect(() => {
    if (tenant) {
      form.reset({
        name: tenant.name,
        contactEmail: tenant.ownerEmail || "",
      });
    }
  }, [tenant, form]);

  const onSubmit = async (data: EditTenantFormData) => {
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;

    try {
      await updateTenant.mutateAsync({ name: data.name });
      if (onSuccess) {
        onSuccess();
      }
    } catch {
      // Error handled by the mutation's own toast; unlatch so a corrected resubmit can go through.
      isSubmittingRef.current = false;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-32" />
      </div>
    );
  }

  if (!tenant) {
    return <div className="text-muted-foreground">Tenant not found</div>;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <fieldset disabled={updateTenant.isPending} className="space-y-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tenant Name</FormLabel>
                <FormControl>
                  <Input placeholder="Acme Corporation" {...field} />
                </FormControl>
                <FormDescription>
                  The name of the organization or tenant
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="contactEmail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Owner Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    disabled
                    placeholder="owner@example.com"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Primary email of the tenant owner (managed via user profile)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end gap-3">
            <Button type="submit" disabled={updateTenant.isPending}>
              {updateTenant.isPending && <Spinner className="mr-2" />}
              {updateTenant.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </fieldset>
      </form>
    </Form>
  );
}
